<template>
    <div ref="chartWrapper" class="relative w-full">
        <canvas ref="chartCanvas" class="w-full"></canvas>
    </div>
</template>

<script setup lang="ts">
    import { useDebounceFn, useThrottleFn, onClickOutside } from '@vueuse/core'
    import { onMounted, ref, watch, computed, onBeforeUnmount, useTemplateRef } from 'vue'
    import { type DatapointObject, AlertStates, UnitType, TemperatureUnit } from '../../types/charts.js'
    import { type TimeZone } from '../../types/time.js'
    import { Chart, type ScriptableContext, ChartOptions, registerables, LineController, ChartDataset } from 'chart.js'
    import annotationPlugin from 'chartjs-plugin-annotation'
    import 'chartjs-adapter-luxon'
    import { DateTime } from 'luxon'
    import theme from '../../tailwindcss-v4/theme.js'
    import chroma from 'chroma-js'
    import { celsiusToFahrenheit } from '../../utils/temperature'

    type Options = {
        decimationAmount: number
        aspectRatio: number
        minHeight: number
        maxHeight: number
        chartLineWeight: {
            sm: number
            md: number
            lg: number
        }
        showGrid: boolean
        showAxes: boolean
        showLimits: boolean
        showTooltip: boolean
        devicePixelRatio?: number
        locale: string
        timeZone: TimeZone
        temperatureUnit: (typeof TemperatureUnit)[keyof typeof TemperatureUnit]
        minTimestamp?: number
        maxTimestamp?: number
    }

    type PartialOptions = Partial<Options>

    const OPTIONS_DEFAULT: Options = {
        decimationAmount: 200,
        aspectRatio: 2,
        minHeight: 250,
        maxHeight: 800,
        chartLineWeight: {
            sm: 3,
            md: 3,
            lg: 4
        },
        showGrid: true,
        showAxes: true,
        showLimits: true,
        showTooltip: true,
        devicePixelRatio: window.devicePixelRatio,
        locale: 'en-US',
        timeZone: 'system' as TimeZone,
        temperatureUnit: TemperatureUnit.Celsius,
        minTimestamp: undefined,
        maxTimestamp: undefined
    } as const

    const props = withDefaults(
        defineProps<{
            alertLevel?: -1 | 0 | 1 | 2
            limits?: {
                min?: number
                max?: number
            }
            unitType?: (typeof UnitType)[keyof typeof UnitType]
            datapoints: DatapointObject[]
            options?: PartialOptions
        }>(),
        {
            datapoints: () => [],
            alertLevel: 0,
            unitType: UnitType.Temperature,
            options: () => ({})
        }
    )
    const options = computed<Options>(() => ({
        ...OPTIONS_DEFAULT,
        ...props.options
    }))

    /**
     * Refs
     */
    const chartWrapper = useTemplateRef<HTMLDivElement>('chartWrapper')
    const chartCanvas = useTemplateRef<HTMLCanvasElement | null>('chartCanvas')
    let lineChart: Chart | null | any = null

    /**
     * Constants
     */
    const unitSuffixes = {
        temperature: {
            celsius: '°C',
            fahrenheit: '°F'
        },
        humidity: '%'
    } as const
    const chartBoundsPerUnitType = {
        temperature: {
            min: -50,
            max: 100
        },
        humidity: {
            min: 0,
            max: 100
        }
    } as const
    const chartBoundsWithoutDataPerUnitType = {
        temperature: {
            min: 0,
            max: 35
        },
        humidity: {
            min: 0,
            max: 100
        }
    } as const

    /**
     * Computed values
     */
    const datapoints = computed(() => props.datapoints)
    const alertState = computed(() => {
        if (props.alertLevel === 0) return AlertStates.Success
        else if (props.alertLevel === 1) return AlertStates.Warning
        else if (props.alertLevel === 2) return AlertStates.Error
        else return AlertStates.Neutral
    })
    const backupChartColor = computed(() => {
        const alertStates = {
            neutral: theme['color-neutral-300'],
            success: theme['color-success-500'],
            warning: theme['color-warning-500'],
            error: theme['color-error-500']
        }
        return alertStates[alertState.value]
    })
    const unitSuffix = computed(() => {
        const suffixOptions = {
            temperature: unitSuffixes.temperature[options.value?.temperatureUnit ?? TemperatureUnit.Celsius],
            humidity: unitSuffixes.humidity
        }
        return suffixOptions[props.unitType]
    })
    const limits = computed(() => props.limits)

    const suggestedMinMaxValues = computed(() => {
        if (!datapoints.value.length) {
            const minChartBound = parseUnitValue(limits.value?.min) ?? parseUnitValue(chartBoundsWithoutDataPerUnitType[props.unitType].min)
            const maxChartBound = parseUnitValue(limits.value?.max) ?? parseUnitValue(chartBoundsWithoutDataPerUnitType[props.unitType].max)
            return { min: minChartBound, max: maxChartBound }
        }

        const selectedDatapoints = datapoints.value.map((datapoint: DatapointObject) => datapoint[props.unitType]).filter((datapoint) => datapoint !== undefined) as number[]
        const maximumDatapoint = parseUnitValue(Math.max(...selectedDatapoints))
        const minimumDatapoint = parseUnitValue(Math.min(...selectedDatapoints))
        const offset = Math.max(1, Math.abs(maximumDatapoint) * 0.02)

        const calculateSuggestedMinimum = () => {
            const suggestedMin = minimumDatapoint - offset

            // Only suggest value within bounds
            const minChartBound = parseUnitValue(chartBoundsPerUnitType[props.unitType].min)
            if (suggestedMin < minChartBound) return minChartBound
            else if (!options.value?.showLimits) return suggestedMin
            else return Math.min(suggestedMin, parseUnitValue(limits.value?.min) ?? suggestedMin)
        }
        const calculateSuggestedMaximum = () => {
            const suggestedMax = maximumDatapoint + offset

            // Only suggest value within bounds
            const maxChartBound = chartBoundsPerUnitType[props.unitType].max
            if (suggestedMax > maxChartBound) return maxChartBound
            else if (!options.value?.showLimits) return suggestedMax
            else return Math.max(suggestedMax, parseUnitValue(limits.value?.max) ?? suggestedMax)
        }

        return {
            min: calculateSuggestedMinimum(),
            max: calculateSuggestedMaximum()
        }
    })

    const parsedChartData = computed(() => {
        if (!datapoints.value) return []
        const data = datapoints.value?.map((datapoint: DatapointObject) => {
            return {
                x: datapoint.timestamp * 1000,
                y: parseUnitValue(datapoint[props.unitType])
            }
        })

        const minTimestamp = options.value?.minTimestamp ? options.value?.minTimestamp * 1000 : undefined
        const maxTimestamp = options.value?.maxTimestamp ? options.value?.maxTimestamp * 1000 : undefined

        let selectedData = data
        if (minTimestamp) {
            selectedData = data.filter((datapoint) => datapoint.x >= minTimestamp)
        }
        if (maxTimestamp) {
            selectedData = selectedData.filter((datapoint) => datapoint.x <= maxTimestamp)
        }

        return selectedData
    })

    const suggestedMinMaxTimestamps = computed(() => {
        if (parsedChartData.value.length <= 1) return { min: undefined, max: undefined }

        const min = options.value?.minTimestamp ? options.value?.minTimestamp * 1000 : undefined
        const max = options.value?.maxTimestamp ? options.value?.maxTimestamp * 1000 : undefined
        return { min, max }
    })

    const drawChart = () => {
        if (lineChart) {
            lineChart.destroy() // If the chart instance exists, destroy it before creating a new one
        }

        if (chartCanvas.value) {
            lineChart = new Chart(chartCanvas.value, {
                /**
                 * intersectDataVerticalLinePlugin needs to be added first to work
                 */
                plugins: [intersectDataVerticalLinePlugin, singleDataPointLinePlugin],
                type: 'glowingLine',
                data: {
                    datasets: [
                        {
                            animation: false,
                            parsing: false,
                            offset: 100,
                            indexAxis: 'x',
                            data: parsedChartData.value,
                            borderWidth: calculateChartLineWeight(),
                            borderCapStyle: 'round',
                            borderJoinStyle: 'round',
                            //borderColor: colors['chart-color'],
                            // segment: {
                            borderColor: (context: any) => {
                                return getLineGradient(context)
                            },
                            // },
                            pointBorderColor: (context: ScriptableContext<'line'>) => {
                                const value = (context as any).raw?.y
                                return calculateColor(value)
                            },
                            pointHoverBorderColor: (context: ScriptableContext<'line'>) => {
                                const value = (context as any).raw?.y
                                return calculateColor(value)
                            },
                            pointBackgroundColor: theme['color-system-light'],
                            spanGaps: true,
                            pointRadius: 0,
                            hitRadius: 12,
                            tension: 0.1,
                            pointHoverBackgroundColor: theme['color-system-light'],
                            pointHoverBorderWidth: 0, //5,
                            pointHoverRadius: 0 //7
                        }
                    ]
                },
                options: {
                    devicePixelRatio: options.value?.devicePixelRatio ?? window.devicePixelRatio,
                    parsing: false,
                    transitions: {
                        active: {
                            animation: {
                                duration: 0
                            }
                        }
                    },
                    responsive: true,
                    maintainAspectRatio: false,
                    clip: false,
                    animation: {
                        duration: 0
                    },
                    layout: {
                        padding: {
                            right: options.value?.showAxes ? 24 : 10
                        }
                    },
                    interaction: {
                        mode: 'index', // recommended for easier chart reading!
                        intersect: false // recommended for easier chart reading!
                    },
                    scales: {
                        x: {
                            type: 'time',
                            adapters: {
                                date: {
                                    locale: options.value?.locale ?? OPTIONS_DEFAULT.locale,
                                    zone: options.value?.timeZone ?? OPTIONS_DEFAULT.timeZone
                                }
                            },
                            time: {
                                displayFormats: {
                                    hour: 'HH:mm',
                                    day: 'MMM dd',
                                    minute: 'HH:mm',
                                    second: 'HH:mm',
                                    millisecond: 'HH:mm'
                                },
                                tooltipFormat: 'HH:mm' // Consistent tooltip format
                            },
                            ticks: {
                                color: theme['text-color-sub'],
                                padding: 10,
                                autoSkip: true,
                                autoSkipPadding: 20,
                                display: options.value?.showAxes,
                                minRotation: 0,
                                maxRotation: 0,
                                font: {
                                    size: remToPixel(theme['text-2xs'])
                                },
                                major: {
                                    enabled: true
                                }
                            },
                            grid: {
                                display: false
                            },
                            border: {
                                display: false
                            },
                            suggestedMin: suggestedMinMaxTimestamps.value.min,
                            suggestedMax: suggestedMinMaxTimestamps.value.max
                        },
                        y: {
                            suggestedMax: suggestedMinMaxValues.value.max,
                            suggestedMin: suggestedMinMaxValues.value.min,
                            ticks: {
                                color: theme['text-color-sub'],
                                beginAtZero: false,
                                padding: options.value?.showAxes ? 10 : 5,
                                callback: (value: any) => {
                                    if (!options.value?.showAxes) return ``
                                    return `${value}${unitSuffix.value}`
                                },
                                display: true,
                                precision: 1,
                                stepSize: 1,
                                minRotation: 0,
                                maxRotation: 0,
                                font: {
                                    size: remToPixel(theme['text-2xs'])
                                }
                            },
                            grid: {
                                display: options.value?.showGrid,
                                drawTicks: false,
                                color: theme['color-surface-border']
                            },
                            border: {
                                dash: [3, 3],
                                display: false
                            }
                        }
                    },
                    plugins: {
                        annotation: {
                            annotations: limitAnnotations.value
                        },
                        decimation: {
                            enabled: true,
                            algorithm: 'lttb',
                            samples: options.value?.decimationAmount,
                            threshold: options.value?.decimationAmount
                        },
                        legend: {
                            display: false
                        },
                        tooltip: {
                            enabled: options.value?.showTooltip,
                            caretPadding: 10, // Tooltip offset
                            yAlign: (context) => {
                                const datapoint = context.tooltip.dataPoints[0].raw as { x: number; y: number }
                                const verticalCenter = (suggestedMinMaxValues.value.min + suggestedMinMaxValues.value.max) / 2
                                return datapoint?.y > verticalCenter ? 'top' : 'bottom'
                            },
                            animation: {
                                duration: 50
                            },
                            offset: 10,
                            mode: 'nearest',
                            axis: 'x',
                            intersect: false,
                            backgroundColor: theme['color-system-dark'],
                            displayColors: true,
                            usePointStyle: true,
                            boxWidth: 8,
                            boxHeight: 3,
                            boxPadding: 4,
                            borderWidth: 6,
                            titleColor: theme['text-color-inactive'],
                            titleFont: {
                                size: remToPixel(theme['text-xs']),
                                weight: 'normal'
                            },
                            titleMarginBottom: 2,
                            bodyColor: theme['color-system-light'],
                            bodyFont: {
                                size: remToPixel(theme['text-xs']),
                                weight: 'bold'
                            },
                            bodySpacing: 0,
                            footerSpacing: 0,
                            footerMarginTop: 0,
                            padding: {
                                top: 6,
                                left: 8,
                                bottom: 6,
                                right: 8
                            },
                            cornerRadius: 4,
                            caretSize: 7,
                            callbacks: {
                                title: (context: { parsed: { x: number; y: number } }[]) => {
                                    const timestamp = context[0].parsed.x
                                    const timeZone = options.value?.timeZone ?? OPTIONS_DEFAULT.timeZone
                                    const locale = options.value?.locale ?? OPTIONS_DEFAULT.locale
                                    const event = DateTime.fromMillis(timestamp).setZone(timeZone).setLocale(locale)

                                    const formattedDate = event.toLocaleString({
                                        day: '2-digit',
                                        month: 'short',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        hour12: false
                                    })

                                    return formattedDate
                                },
                                label: (context: { parsed: { x: number; y: number } }) => {
                                    return `${context.parsed?.y}${unitSuffix.value}`
                                }
                            }
                        }
                    }
                } as ChartOptions
            })
        }
    }

    /**
     * Lifecycle logic
     */
    onMounted(async () => {
        if (chartWrapper.value) {
            drawChart()
            resizeObserver.observe(document.body)
        }
    })
    onBeforeUnmount(() => {
        if (chartWrapper.value) {
            resizeObserver.unobserve(document.body)
        }
    })

    /**
     * Watchers
     */

    // Reactively watch the `chartData` prop for changes and redraw the chart
    const throttledDrawChart = useThrottleFn(drawChart, 50)
    const debouncedDrawChart = useDebounceFn(drawChart, 100)
    watch(
        () => [datapoints.value, limits.value, options.value?.temperatureUnit],
        () => {
            throttledDrawChart()
            debouncedDrawChart()
        },
        { deep: true }
    )

    const resizeChart = () => {
        if (lineChart && chartWrapper.value) {
            const width = chartWrapper.value?.clientWidth || window.innerWidth
            const chartLineWeight = calculateChartLineWeight()
            const aspectRatio = options.value?.aspectRatio
            const minHeight = options.value?.minHeight
            const maxHeight = options.value?.maxHeight

            // Update chart line weight
            lineChart.data.datasets.forEach((dataset: ChartDataset) => {
                dataset.borderWidth = chartLineWeight
            })

            let height
            if (maxHeight) height = Math.min(Math.max(width / aspectRatio, minHeight), maxHeight)
            else height = Math.max(width / aspectRatio, minHeight)

            lineChart.resize(width, height)
        }
    }

    // Backup resize for initial load in desktop Safari
    window.addEventListener('load', () => {
        resizeChart()
    })

    // Debounce added to solve issue in desktop Safari
    const resizeObserver = new ResizeObserver(
        useDebounceFn(() => {
            resizeChart()
        }, 1)
    )

    /**
     * Tooltip logic
     */
    const hideTooltip = () => {
        if (lineChart) {
            lineChart.tooltip.setActiveElements([], { x: 0, y: 0 })
            lineChart.setActiveElements([], { x: 0, y: 0 })
            lineChart.update()
        }
    }
    onClickOutside(chartCanvas, () => hideTooltip())

    /**
     * Helper functions
     */

    const calculateChartLineWeight = () => {
        const width = chartWrapper.value?.clientWidth || window.innerWidth
        switch (true) {
            case width < 480:
                return options.value?.chartLineWeight?.sm ?? OPTIONS_DEFAULT.chartLineWeight.sm
            case width < 800:
                return options.value?.chartLineWeight?.md ?? OPTIONS_DEFAULT.chartLineWeight.md
            default:
                return options.value?.chartLineWeight?.lg ?? OPTIONS_DEFAULT.chartLineWeight.lg
        }
    }

    const calculateColor = (value: number) => {
        if (!limits.value?.min || !limits.value?.max) return backupChartColor.value
        const upperLimit = parseUnitValue(limits.value?.max) + 1
        const lowerLimit = parseUnitValue(limits.value?.min) - 1
        const limitRange = upperLimit - lowerLimit
        const stopStep = Math.min(0.25, 1 / limitRange)
        // TODO: handle stop step same as in getLineGradient

        const color = chroma
            .scale([
                theme['color-error-500'],
                theme['color-warning-500'],
                theme['color-success-500'],
                theme['color-success-500'],
                theme['color-warning-500'],
                theme['color-error-500']
            ])
            .domain([lowerLimit, lowerLimit + 1, lowerLimit + 2, upperLimit - 2, upperLimit - 1, upperLimit])
            .mode('lab')
        return color(value).hex() as string
    }

    const getLineGradient = (context: ScriptableContext<'line'>) => {
        if (!limits.value?.min || !limits.value?.max) return backupChartColor.value

        const chart = context.chart
        const { ctx, chartArea } = chart

        // const value = (context as any).p1.parsed.y
        if (!chartArea) return

        let gradient
        if (!gradient) {
            const yAxis = chart.scales['y']

            // TODO: color gradient is slightly different between celsius and fahrenheit
            const upperLimit = parseUnitValue(limits.value?.max) + 1
            const lowerLimit = parseUnitValue(limits.value?.min) - 1
            const limitRange = upperLimit - lowerLimit
            const stopStep = Math.min(0.25, 1 / limitRange)
            const yTop = yAxis.getPixelForValue(upperLimit)
            const yBottom = yAxis.getPixelForValue(lowerLimit)

            gradient = ctx.createLinearGradient(0, yBottom, 0, yTop)
            gradient.addColorStop(0, theme['color-error-500'])
            gradient.addColorStop(0 + stopStep, theme['color-warning-500'])
            gradient.addColorStop(0 + stopStep * 2, theme['color-success-500'])
            gradient.addColorStop(1 - stopStep * 2, theme['color-success-500'])
            gradient.addColorStop(1 - stopStep, theme['color-warning-500'])
            gradient.addColorStop(1, theme['color-error-500'])
        }

        return gradient
    }

    const parseUnitValue = (value?: number | null): number => {
        if (!value || isNaN(value)) return 0
        return props.unitType === UnitType.Temperature && options.value?.temperatureUnit === TemperatureUnit.Fahrenheit ? celsiusToFahrenheit(value) : value
    }

    const remToPixel = (themeValue: string) => {
        return parseFloat(themeValue) * 16
    }

    /**
     * Chart plugins
     */
    const intersectDataVerticalLinePlugin = {
        id: 'intersectDataVerticalLine',
        beforeDatasetsDraw: (chart: Chart) => {
            if (!options.value?.showTooltip) return
            if (chart.getActiveElements().length) {
                const activePoint = chart.getActiveElements()[0]
                const xPosition = activePoint.element.x
                const yPosition = activePoint.element?.y

                // Get the color from the active dataset
                // const value = (chart.getActiveElements()[0].element as any).raw?.y
                const value = (chart.getActiveElements()[0].element as any)?.$context?.raw?.y
                const color = calculateColor(value)

                const chartArea = chart.chartArea
                const ctx: CanvasRenderingContext2D = chart.ctx
                ctx.save()

                // Grey line
                ctx.beginPath()
                ctx.moveTo(xPosition, chartArea.top)
                ctx.lineTo(xPosition, chartArea.bottom)
                ctx.lineWidth = 2
                ctx.strokeStyle = theme['color-surface-border']
                ctx.stroke()
                ctx.restore()

                // Colored line
                ctx.beginPath()
                ctx.moveTo(xPosition, yPosition)
                ctx.lineTo(xPosition, chartArea.bottom)
                ctx.lineWidth = 2
                ctx.strokeStyle = color //colors['chart-color']
                ctx.stroke()
                ctx.restore()
            }
        }
    }

    const singleDataPointLinePlugin = {
        id: 'singleDataPointLine',
        afterDatasetsDraw: (chart: Chart) => {
            const { ctx, data, scales } = chart
            const { x, y } = scales

            if (!data.datasets || data.datasets.length === 0) return

            data.datasets.forEach((dataset: any) => {
                if (!dataset.data || dataset.data.length !== 1) return

                const dataPoint = dataset.data[0]

                if (dataPoint && dataPoint.x && dataPoint.y) {
                    const xValue = dataPoint.x
                    const yValue = dataPoint.y

                    const xPixel = x.getPixelForValue(xValue)
                    const yPixel = y.getPixelForValue(yValue)

                    ctx.save()
                    ctx.beginPath()
                    ctx.moveTo(x.getPixelForValue(x.min), yPixel) // Start at the left edge
                    ctx.lineTo(x.getPixelForValue(x.max), yPixel) // End at the right edge

                    ctx.lineWidth = calculateChartLineWeight()
                    ctx.strokeStyle = calculateColor(yValue)
                    ctx.lineCap = 'round'
                    ctx.lineJoin = 'round'
                    ctx.stroke()
                    ctx.restore()
                }
            })
        }
    }

    /**
     * Glowing line controller
     */
    class GlowingLineController extends LineController {
        draw() {
            const ctx = this.chart.ctx

            // Iterate through each dataset
            this.chart.data.datasets.forEach((dataset, index) => {
                const meta = this.chart.getDatasetMeta(index)

                if (meta.controller === this) {
                    // Check if the dataset should have the shadow effect
                    // Draw the blurred version
                    ctx.save()
                    ctx.filter = 'blur(12px)'
                    ctx.globalAlpha = 0.5
                    super.draw.apply(this, arguments as any)
                    ctx.restore() // Restore context to remove blur

                    // Draw the unblurred version
                    super.draw.apply(this, arguments as any)
                }
            })
        }
    }
    GlowingLineController.id = 'glowingLine'
    GlowingLineController.defaults = LineController.defaults

    Chart.register(...registerables, annotationPlugin, GlowingLineController)

    // Add the type definition to make TypeScript happy
    declare module 'chart.js' {
        interface ChartTypeRegistry {
            glowingLine: ChartTypeRegistry['line']
        }
    }

    /**
     * Limit annotations
     */
    const limitAnnotationConfig = {
        type: 'line',
        borderColor: theme['color-error-500'],
        borderWidth: 1,
        borderDash: [3, 3],
        borderCapStyle: 'round',
        drawTime: 'beforeDatasetsDraw',
        label: {
            //hack, otherwise conversion to svg fails
            display: false,
            content: ''
        }
    }

    const limitAnnotations = computed(() => {
        if (!options.value?.showLimits) return
        return {
            annotationMax: {
                ...limitAnnotationConfig,
                yMin: parseUnitValue(limits.value?.max),
                yMax: parseUnitValue(limits.value?.max),
                display: limits.value?.max ? !isNaN(limits.value?.max) : false
            },
            annotationMin: {
                ...limitAnnotationConfig,
                yMin: parseUnitValue(limits.value?.min),
                yMax: parseUnitValue(limits.value?.min),
                display: limits.value?.min ? !isNaN(limits.value?.min) : false
            }
        }
    })
</script>
